import React, { useCallback, useEffect, useState } from 'react';
import { useTheme } from '@material-ui/core/styles';
import {
  Button,
  CircularProgress,
  IconButton,
  Paper,
  TextField,
  Tooltip,
  Typography,
} from '@material-ui/core';
import OpenInBrowserIcon from '@material-ui/icons/OpenInBrowser';
import StopIcon from '@material-ui/icons/Stop';
import { useApi, discoveryApiRef, identityApiRef } from '@backstage/core-plugin-api';
import {
  matlabVncSessionApiRef,
  PersistedSessionState,
} from './matlabVncSessionApi';

// ── Floating card ─────────────────────────────────────────────────────────────

/**
 * Floating session status card for a specific session.
 *
 * Accepts a sessionId prop to know which session to display.
 * Pass this sessionId when rendering from CatalogHomePage.
 */
export function MatlabVncSessionCard({ sessionId }: { sessionId: string }) {
  const sessionApi = useApi(matlabVncSessionApiRef);
  const discoveryApi = useApi(discoveryApiRef);
  const identityApi = useApi(identityApiRef);
  const theme = useTheme();

  const [session, setSession] = useState<PersistedSessionState | null>(() => {
    const all = sessionApi.getAllSessions();
    return all.find(s => s.sessionId === sessionId) ?? null;
  });
  const [showEnd, setShowEnd] = useState(false);
  const [comment, setComment] = useState('');
  const [ending, setEnding] = useState(false);

  // Keep local state in sync with the API singleton
  useEffect(
    () =>
      sessionApi.subscribe((allSessions: PersistedSessionState[]) => {
        const found = allSessions.find(s => s.sessionId === sessionId) ?? null;
        setSession(found);
        if (!found) {
          setShowEnd(false);
          setComment('');
        }
      }),
    [sessionApi, sessionId],
  );

  const handleEnd = useCallback(async () => {
    setEnding(true);
    try {
      const baseUrl = await discoveryApi.getBaseUrl('matlab-vnc');
      const { token } = await identityApi.getCredentials();
      await sessionApi.endSession(comment, baseUrl, token, sessionId);
    } finally {
      setEnding(false);
    }
  }, [sessionApi, discoveryApi, identityApi, comment, sessionId]);

  // Nothing to show when no session is minimized
  if (!session) return null;

  const successColor = (theme.palette as any).success?.main ?? '#4caf50';

  const card = (
    <>
      {/* Keyframe for the pulsing status dot */}
      <style>{`
        @keyframes matlabVncPulse {
          0%, 100% { opacity: 1; transform: scale(1); }
          50%       { opacity: 0.4; transform: scale(0.85); }
        }
      `}</style>

      <Paper
        elevation={8}
        style={{
          position: 'relative',
          width: 300,
          padding: '12px 14px',
          borderRadius: 8,
          borderLeft: `4px solid ${successColor}`,
          boxSizing: 'border-box',
        }}
      >
        {/* ── Header row ── */}
        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          {/* Pulsing status dot */}
          <span
            style={{
              display: 'inline-block',
              width: 8,
              height: 8,
              borderRadius: '50%',
              background: successColor,
              flexShrink: 0,
              animation: 'matlabVncPulse 2.2s ease-in-out infinite',
            }}
          />

          <div style={{ flex: 1, minWidth: 0 }}>
            <Typography
              variant="caption"
              style={{
                fontWeight: 700,
                fontSize: 10,
                color: theme.palette.text.secondary,
                textTransform: 'uppercase',
                letterSpacing: '0.6px',
                display: 'block',
              }}
            >
              MATLAB Active
            </Typography>
            <Typography
              variant="body2"
              style={{
                fontWeight: 600,
                fontSize: 13,
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap',
              }}
            >
              {session.folderName}
            </Typography>
          </div>

          {/* Quick link to open VNC tab */}
          <Tooltip title="Open VNC in new tab">
            <span>
              <IconButton
                size="small"
                onClick={() =>
                  session.vncPath && window.open(session.vncPath, '_blank')
                }
                disabled={!session.vncPath}
                style={{ padding: 4 }}
              >
                <OpenInBrowserIcon fontSize="small" />
              </IconButton>
            </span>
          </Tooltip>
        </div>

        {/* ── Action row ── */}
        {!showEnd ? (
          <div style={{ marginTop: 10 }}>
            <Button
              size="small"
              variant="outlined"
              fullWidth
              startIcon={<StopIcon style={{ fontSize: 16 }} />}
              onClick={() => setShowEnd(true)}
              style={{
                fontSize: 12,
                padding: '6px 8px',
                color: theme.palette.error.main,
                borderColor: theme.palette.error.main,
              }}
            >
              End Session
            </Button>
          </div>
        ) : (
          /* ── Inline end-session form ── */
          <div style={{ marginTop: 10 }}>
            <TextField
              label="What changed? (optional)"
              variant="outlined"
              fullWidth
              size="small"
              multiline
              rows={2}
              value={comment}
              onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                setComment(e.target.value)
              }
              autoFocus
              style={{ marginBottom: 8 }}
            />
            <div
              style={{
                display: 'flex',
                gap: 6,
                justifyContent: 'flex-end',
              }}
            >
              <Button
                size="small"
                onClick={() => setShowEnd(false)}
                disabled={ending}
              >
                Cancel
              </Button>
              <Button
                size="small"
                variant="contained"
                color="secondary"
                startIcon={
                  ending ? (
                    <CircularProgress size={14} style={{ color: 'inherit' }} />
                  ) : (
                    <StopIcon style={{ fontSize: 16 }} />
                  )
                }
                onClick={handleEnd}
                disabled={ending}
              >
                {ending ? 'Ending…' : 'Confirm End'}
              </Button>
            </div>
          </div>
        )}
      </Paper>
    </>
  );

  // Return card directly (parent container handles portal to document.body)
  return card;
}
